/**
 * Which entities an action needs to already exist, and which it creates.
 *
 * This is the frontend mirror of the backend's create-before-reference rules
 * (`TimelineEventActionHandler._referenced_entity_ids` /
 * `find_dependencies` in
 * `apps/crm_events/actions/timeline_event_action_handler.py`). Keep the two in
 * step: the backend prunes execution with its copy, so a divergence here shows
 * up as the UI promising something the server then silently skips.
 *
 * The rules, per event type:
 *   - `ahoy_object_created`   → references nothing; CREATES its entity.
 *   - `ahoy_property_updated` → references its own entity.
 *   - `ahoy_object_deleted`   → references its own entity.
 *   - `ahoy_association_*`    → references BOTH endpoints (source + target).
 *   - `ahoy_object_merged`    → references the merge target (its own entity)
 *                               and the merge source.
 *
 * Non-timeline actions (email sends, calendar events, onboarding) reference no
 * CRM entity and create none, so they are never gated by this.
 */

import type { ProposedAction } from '../core/types';
import {
  isAssociationPayload,
  isObjectCreatedPayload,
  isObjectDeletedPayload,
  isObjectMergedPayload,
  isPropertyUpdatePayload,
} from '../core/types';

/** No references — hoisted so callers get a stable empty result. */
const NO_REFS: readonly string[] = [];

/**
 * Entity ids that must already exist (or be created by another selected
 * action) before `action` can be applied.
 */
export function getReferencedEntityIds(
  action: ProposedAction
): readonly string[] {
  const { payload } = action;

  // A create depends on nothing — it is what brings the entity into being.
  if (isObjectCreatedPayload(payload)) {
    return NO_REFS;
  }

  if (isAssociationPayload(payload)) {
    // Both endpoints must exist: the source carries the association, the
    // target is what it points at.
    const target = payload.payload.target_entity_id;
    return target
      ? [String(payload.entity_id), String(target)]
      : [String(payload.entity_id)];
  }

  if (isObjectMergedPayload(payload)) {
    // The merge target is the action's own entity; the source is absorbed.
    const source = payload.payload.source_entity_id;
    return source
      ? [String(payload.entity_id), String(source)]
      : [String(payload.entity_id)];
  }

  if (isPropertyUpdatePayload(payload) || isObjectDeletedPayload(payload)) {
    return [String(payload.entity_id)];
  }

  return NO_REFS;
}

/**
 * The entity id `action` creates, or `null` when it creates nothing.
 *
 * Only `ahoy_object_created` creates. This is the gate PR #13271 added to the
 * serializer after a no-op `ahoy_property_updated` on a PRE-EXISTING deal was
 * treated as that deal's creator, which cascaded a deselect onto every action
 * that merely referenced it.
 */
export function getCreatedEntityId(action: ProposedAction): string | null {
  const { payload } = action;
  return isObjectCreatedPayload(payload) ? String(payload.entity_id) : null;
}
